import { tint } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { pxInRem, tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { Colors, toRem } from '../../../ui';
import { ElementSelector } from '../../canvas/ui/ElementSelector';
import { Flexer } from '../../dashboard/elements/LayoutElements';

type BorderValues = [number, number, number, number];

const ZoneFrame = styled.div`
    position: relative;
    flex: 1 1 auto;
    min-height: 7rem;
    min-width: 9rem;
`;

const ZoneBorder = styled.div<{ inset?: string, border: BorderValues, color: string, dashed?: boolean }>`
    position: absolute;
    inset: ${p => toRem(p.inset ?? 0)};
    padding: .25rem;
    border-radius: .25rem;
    background: #fff;
    border: ${p => p.dashed ? 'dashed' : 'solid'} .125rem ${Colors.stone300};
    ${p => p.border[0] && css`border-left: solid ${tint(.5, p.color)} ${pxInRem(p.border[0])}rem`};
    ${p => p.border[1] && css`border-top: solid ${tint(.5, p.color)} ${pxInRem(p.border[1])}rem`};
    ${p => p.border[2] && css`border-right: solid ${tint(.5, p.color)} ${pxInRem(p.border[2])}rem`};
    ${p => p.border[3] && css`border-bottom: solid ${tint(.5, p.color)} ${pxInRem(p.border[3])}rem`};
`;

const ZoneText = styled.div<{ width: any, color?: string }>`
    display: inline-block;
    width: ${p => toRem(p.width)};
    height: .375rem;
    margin-right: .25rem;
    background: ${p => p.color ?? Colors.stone300};
    border-radius: .125rem;
    vertical-align: top;
`;

const Simple = React.memo<{ border: BorderValues, color: string }>(({ border, color }) => {
    border = border.map(x => x * .33) as BorderValues;
    return <ZoneFrame>
        <ZoneBorder {...{ border, color }}>
            <ZoneText width=".375" />
            <ZoneText width="4" />
        </ZoneBorder>
    </ZoneFrame>;
});

const Margin = React.memo<any>(({ border, color }) => {
    border = border.map(x => x * .25);
    return <ZoneFrame>
        <ZoneBorder {...{ border, color }}>
            <ZoneText width=".375" />
            <ZoneText width="4" />
        </ZoneBorder>
    </ZoneFrame>;
});

const Topped = React.memo<any>(({ border, color }) => {
    border = border.map((x, i) => x * (i === 1 ? .25 : .33));

    return <ZoneFrame>
        <ZoneBorder {...{ border, color }} />
        <Flexer inset=".25 0 0 0" flex="row" justify="center">
            <ZoneText width=".375" color="#fff" />
            <ZoneText width="4" color="#fff" />
        </Flexer>
    </ZoneFrame>;
});

const Titled = React.memo<any>(({ border, color }) => {
    border = border.map((x, i) => x * (i === 1 ? .25 : .33));
    return <ZoneFrame>
        <ZoneBorder inset="0" {...{ border, color }} />
        <Flexer inset=".25 0 0 0" flex="column" align="center" justify="flex-start">
            <Flexer flex="row" background={tintSafe(.5, color)} padding=".25" borderRadius=".25">
                <ZoneText width=".375" color="#fff" />
                <ZoneText width="4" color="#fff" />
            </Flexer>
        </Flexer>
    </ZoneFrame>;
});

const defaultsZoneProps = {
    color: '',
    border: [0, 0, 0, 0],
    headingPosition: 'left',
    headingOffset: 0,
    titleColor: '',
    headingStyle: 'classic',
};

const ZoneItem = (label: string, props: any, preview: React.ComponentType<any>) =>
    ElementSelector.Item(`zones/${label}`, label, { ...defaultsZoneProps, ...props }, ElementSelector.ItemPreview(preview));

export const ZonePreview = { Simple, Margin, Topped, Titled };

export const ZoneElementSelectorItems = (color: string) => [
    ZoneItem('Classic', { color, border: [0, 0, 0, 0] }, ZonePreview.Simple),
    ZoneItem('Bordered', { color, border: [10, 10, 10, 10] }, ZonePreview.Simple),
    ZoneItem('Margin Left', { color, border: [50, 0, 0, 0] }, ZonePreview.Margin),
    ZoneItem('Margin Right', { color, border: [0, 0, 50, 0] }, ZonePreview.Margin),
    ZoneItem('Topped', { color, border: [10, 60, 10, 10], headingPosition: 'center', headingOffset: -60, titleColor: '#fff' }, ZonePreview.Topped),
    ZoneItem('Titled', { color, border: [0, 0, 0, 0], headingPosition: 'center', headingStyle: 'fill', headingOffset: 0 }, ZonePreview.Titled),
];


